import { get, writable } from 'svelte/store';
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { ChartDataInterface, ChartStatisticInterface } from 'src/types/Chart';
import { buildEnergyRangeControls, type EnergyMonitoringPeriodId } from './period_helpers';
import type { TariffBucket } from './energy_tariff_helpers';
import {
	energyMonitoringDataMode,
	loadEnergyMonitoringDataMode,
	setEnergyMonitoringDataMode,
	type EnergyMonitoringDataMode,
} from './energy_data_mode';

export type { EnergyMonitoringDataMode };
export { energyMonitoringDataMode, setEnergyMonitoringDataMode };

export type EnergyMonitoringStats = {
	loading: boolean;
	error: boolean;
	chartData: ChartDataInterface | null;
	statistic: ChartStatisticInterface | null;
	/** Pre-aggregated tariff buckets; raw API history is not retained. */
	tariffBuckets: TariffBucket[];
};

const PLACEHOLDER_CONTROLS: ChartControlsDataInterface = {
	time: { from: new Date(), to: new Date() },
	format: 'Y-m-d H:i:s',
	rangeOption: {
		period: '24_hours',
		timeRange: '24h',
		timeLabelFormat: 'day',
		enabled: true,
	},
	currentPeriod: '24_hours',
};

export const emptyEnergyMonitoringStats = (): EnergyMonitoringStats => ({
	loading: false,
	error: false,
	chartData: null,
	statistic: null,
	tariffBuckets: [],
});

export const energyMonitoringPeriodId = writable<EnergyMonitoringPeriodId>('live');
export const energyMonitoringControls = writable<ChartControlsDataInterface>(PLACEHOLDER_CONTROLS);
export const energyMonitoringStats = writable<EnergyMonitoringStats>(emptyEnergyMonitoringStats());

/** Deferred — avoids circular import TDZ with chart_control_helpers / reset.ts. */
const initEnergyMonitoringControls = () => {
	energyMonitoringControls.set(buildEnergyRangeControls(get(energyMonitoringPeriodId)));
};

if (typeof queueMicrotask === 'function') {
	queueMicrotask(initEnergyMonitoringControls);
} else {
	setTimeout(initEnergyMonitoringControls, 0);
}

export const resetEnergyMonitoringStats = () => {
	energyMonitoringStats.set(emptyEnergyMonitoringStats());
};

/** Clear in-memory energy state on profile/account switch. */
export const resetEnergyMonitoringForAccountChange = () => {
	resetEnergyMonitoringStats();
	setEnergyMonitoringPeriod('live');
	energyMonitoringDataMode.set(loadEnergyMonitoringDataMode());
};

export const setEnergyMonitoringPeriod = (periodId: EnergyMonitoringPeriodId) => {
	energyMonitoringPeriodId.set(periodId);
	energyMonitoringControls.set(buildEnergyRangeControls(periodId));
};
